/**
 * Shared constants and utilities for dashboard filtering
 */

/** Maps dashboard filter types to URL query param names */
export const FILTER_PARAM_MAP: Record<string, string> = {
    source: 'source',
    referringSource: 'referringSource',
    contactType: 'contactType',
    serviceType: 'serviceType',
    jobType: 'jobType',
    urgency: 'urgency',
    highValueLead: 'highValueLead',
    zipCode: 'zipCode',
    // Lead taxonomy filters. The dashboard query gained these arguments in stentor-api#6042; they
    // are deliberately absent from LEADS_FILTER_MAP below until the leads list reads them, so the
    // "View Leads" handoff doesn't emit a param that page ignores.
    contactMethod: 'contactMethod',
    agentType: 'agentType',
    capturePlatform: 'capturePlatform',
    sourceSystem: 'sourceSystem'
};

/** Maps dashboard filter types to leads list query param names */
export const LEADS_FILTER_MAP: Record<string, string> = {
    source: 'source',
    referringSource: 'referringSource',
    urgency: 'urgency',
    highValueLead: 'highValueLead',
    contactType: 'contactType',
    serviceType: 'serviceType',
    jobType: 'jobType',
    zipCode: 'zipCode'
};

/** Maximum allowed length for filter values */
const MAX_FILTER_VALUE_LENGTH = 200;

/** Pattern for valid filter values - alphanumeric, underscores, hyphens, spaces */
const VALID_FILTER_PATTERN = /^[a-zA-Z0-9_\s-]+$/;

/**
 * Validates a filter value from URL params
 * Returns undefined if the value is invalid or empty
 */
export const validateFilterValue = (value: string | null): string | undefined => {
    if (!value) return undefined;
    if (value.length > MAX_FILTER_VALUE_LENGTH) return undefined;
    if (!VALID_FILTER_PATTERN.test(value)) return undefined;
    return value;
};

/**
 * Validates an organization ID
 * Must be alphanumeric with hyphens/underscores, 1-128 chars
 */
export const isValidOrganizationId = (id: string): boolean => {
    return typeof id === 'string' && id.length > 0 && id.length <= 128 && /^[a-zA-Z0-9_-]+$/.test(id);
};

/**
 * Validates an app ID
 * Must be alphanumeric with hyphens/underscores, 1-128 chars
 */
export const isValidAppId = (id: string): boolean => {
    return typeof id === 'string' && id.length > 0 && id.length <= 128 && /^[a-zA-Z0-9_-]+$/.test(id);
};

/**
 * Parses boolean filter value from URL param
 */
export const parseBooleanFilter = (value: string | null): boolean | undefined => {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return undefined;
};
